// platform-push-notifications: "התראות" — drawer מהתפריט (?notify=1), כמו "חיבור Gmail". מצב במכשיר הזה, הפעלה (רק בלחיצה —
// כאן הדפדפן שואל), כיבוי, התראת בדיקה, וכיבוי לכל סוג (לכל המכשירים של המשתמש). באייפון בלי "הוסף למסך הבית" — הסבר.
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { browserSupport, disablePush, enablePush, iosNeedsInstall, pushApi, PushKind } from '../lib/push.ts';
import { T } from '../lib/strings.ts';
import { ErrorView, Loading } from './Status.tsx';
import { StatusLabel } from './ui/badge.tsx';
import { Button } from './ui/button.tsx';
import { Sheet } from './ui/sheet.tsx';
import { IconBell, IconX } from './brand/icons.tsx';

export const NOTIFY_PARAM = 'notify';

export function NotificationsDrawer() {
  const [sp, setSp] = useSearchParams();
  const open = sp.get(NOTIFY_PARAM) === '1';
  const close = useCallback(
    () =>
      setSp((p) => {
        const n = new URLSearchParams(p);
        n.delete(NOTIFY_PARAM);
        return n;
      }),
    [setSp],
  );
  return (
    <Sheet open={open} onClose={close} title={T.push.title} side="start" className="max-w-md" testId="notify-drawer">
      <div className="flex items-center gap-2 border-b border-border p-3 ps-4">
        <IconBell size={20} className="text-primary" />
        <h2 className="m-0 me-auto text-lg font-bold">{T.push.title}</h2>
        <Button variant="ghost" size="icon" aria-label={T.push.close} title={T.push.close} onClick={close} data-testid="notify-close">
          <IconX size={20} />
        </Button>
      </div>
      {open && <Body />}
    </Sheet>
  );
}

function Body() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['push'], queryFn: pushApi.info, staleTime: 0 });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const support = browserSupport();
  const refresh = () => qc.invalidateQueries({ queryKey: ['push'] });

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorView error={q.error} onRetry={() => q.refetch()} />;
  const cfg = q.data.config;
  const on = !!q.data.subscribed && support === 'ok' && Notification.permission === 'granted';

  const run = async (fn: () => Promise<string | null>) => {
    setBusy(true);
    setNote(null);
    try {
      setNote(await fn());
    } catch {
      setNote(T.push.failed);
    } finally {
      setBusy(false);
      void refresh();
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="notify-body">
      <p className="m-0 text-sm">{T.push.intro}</p>
      {!cfg.enabled ? (
        <p className="m-0 text-sm text-muted-foreground" data-testid="notify-disabled">{T.push.disabled}</p>
      ) : support === 'unsupported' ? (
        <p className="m-0 text-sm" data-testid="notify-unsupported">{iosNeedsInstall() ? T.push.ios : T.push.unsupported}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <StatusLabel tone={on ? 'ok' : 'neutral'} data-testid="notify-state" data-on={on ? '1' : '0'}>
              {on ? T.push.on : T.push.off}
            </StatusLabel>
            {!!q.data.devices && <span className="text-xs text-muted-foreground">{T.push.devices(q.data.devices)}</span>}
          </div>
          {support === 'denied' && <p className="m-0 text-sm" data-testid="notify-denied">{T.push.denied}</p>}
          <div className="flex flex-wrap gap-2">
            {on ? (
              <>
                <Button variant="outline" disabled={busy} onClick={() => run(async () => (await pushApi.test(), T.push.testSent))} data-testid="notify-test">
                  {T.push.test}
                </Button>
                <Button variant="ghost" disabled={busy} onClick={() => run(async () => (await disablePush(), null))} data-testid="notify-disable">
                  {T.push.disable}
                </Button>
              </>
            ) : (
              support === 'ok' && (
                <Button disabled={busy} onClick={() => run(async () => ((await enablePush(cfg)) === 'denied' ? T.push.deniedNow : null))} data-testid="notify-enable">
                  {T.push.enable}
                </Button>
              )
            )}
          </div>
          {note && <p className="m-0 text-sm text-muted-foreground" role="status" data-testid="notify-note">{note}</p>}
          {q.data.prefs && (
            <fieldset className="m-0 flex flex-col gap-1 border-0 p-0">
              <legend className="mb-1 text-sm font-semibold">{T.push.kindsTitle}</legend>
              {PushKind.options.map((k) => (
                <label key={k} className="flex min-h-11 cursor-pointer items-center gap-3 rounded-sm px-1 text-sm hover:bg-muted">
                  <input
                    type="checkbox"
                    className="size-4 accent-[var(--color-action)]"
                    checked={q.data.prefs![k] !== false}
                    onChange={(e) => {
                      const v = e.target.checked;
                      // אופטימי: התיבה מתעדכנת מיד, השרת מאשר ברקע (כשל — רענון מחזיר את המצב האמיתי)
                      qc.setQueryData(['push'], { ...q.data, prefs: { ...q.data.prefs!, [k]: v } });
                      void pushApi.setPrefs({ [k]: v }).catch(() => undefined).finally(refresh);
                    }}
                    data-testid={`notify-kind-${k}`}
                  />
                  {T.push.kinds[k]}
                </label>
              ))}
            </fieldset>
          )}
          <p className="m-0 text-xs text-muted-foreground">{T.push.privacy}</p>
        </>
      )}
    </div>
  );
}
